import React, { useState } from 'react';
import PropTypes from 'prop-types';
import StarRatings from 'react-star-ratings';
import * as echarts from 'echarts';
import ReactEcharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import get from 'lodash/get';

import Select from '../../common/inputs/Select';
import useStyles from './styles';


const QuestionRatingChart = ({ data }) => {
  const [selectedQuestion, setSelectedQuestion] = useState('0');
  const { t } = useTranslation(['translation']);
  const classes = useStyles();

  const answers = get(data[selectedQuestion], 'answers', [])
    .map((a) => ({ name: a.name, value: a.count }));

  const totalAnswers = answers.reduce((acc, cv) => acc + cv.value, 0);
  const scoreSum = answers.reduce((acc, cv) => acc + cv.value * cv.name, 0);
  const averageRating = totalAnswers ? scoreSum / totalAnswers : 0;

  const generateTooltipText = ([{ name, value }]) => {
    const stars = parseInt(name, 10);
    return `
      ${t('QUESTIONNAIRE.STAR_WITH_COUNT', { count: stars })}<br/>
      ${t('QUESTIONNAIRE.ANSWER_WITH_COUNT', { count: value })}<br/>
      ${t('COMMON.INTL_PERCENT', { value: totalAnswers ? value / totalAnswers : 0 })}`
  };

  const options = {
    backgroundColor: 'white',
    yAxis: {
      type: 'category',
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: '#7A75A0',
        fontFamily: 'Poppins, sans-serif',
        fontSize: 12,
        fontWeight: 'bold',
      },
      data: answers.map(el => el.name),
    },
    xAxis: {
      type: 'value',
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: '#7A75A0',
        fontFamily: 'Poppins, sans-serif',
        fontSize: 12,
        fontWeight: 'bold',
      },
      splitLine: {
        show: false,
      },
    },
    tooltip: {
      show: true,
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      position: 'top',
      backgroundColor: 'rgba(50, 50, 50, 0.7)',
      formatter: generateTooltipText,
      textStyle: {
        color: 'white',
        fontFamily: 'Poppins, sans-serif',
        fontSize: 24,
        fontWeight: 600,
        textAlign: 'center',
        align: 'center',
      },
      confine: true,
    },
    series: {
      type: 'bar',
      barWidth: '80%',
      data: answers,
      colorBy: 'data',
    },
    color: [
      new echarts.graphic.LinearGradient(
        1, 0, 0, 0,
        [{ offset: 0, color: '#a80000' }, { offset: 1, color: '#d40000' }],
      ),
      new echarts.graphic.LinearGradient(
        1, 0, 0, 0,
        [{ offset: 0, color: '#ff0000' }, { offset: 1, color: '#ff4242' }],
      ),
      new echarts.graphic.LinearGradient(
        1, 0, 0, 0,
        [{ offset: 0, color: '#ff5900' }, { offset: 1, color: '#ff8340' }],
      ),
      new echarts.graphic.LinearGradient(
        1, 0, 0, 0,
        [{ offset: 0, color: '#fffb00' }, { offset: 1, color: '#fffd66' }],
      ),
      new echarts.graphic.LinearGradient(
        1, 0, 0, 0,
        [{ offset: 0, color: '#5eff00' }, { offset: 1, color: '#afff80' }],
      ),
      new echarts.graphic.LinearGradient(
        1, 0, 0, 0,
        [{ offset: 0, color: '#009c1d' }, { offset: 1, color: '#46b85b' }],
      ),
    ],
  };

  const questions = data.map(({ question }, idx) => ({ value: idx.toString(), label: question }));

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <Select
          id="rating"
          value={questions.length ? selectedQuestion : null}
          setFieldValue={(_, value) => setSelectedQuestion(value)}
          options={questions}
          iconName="name"
        />
      </div>
      <div className={classes.mainSection}>
        <div className={classes.starRatingWrapper}>
          <div className={classes.ratingValue}>
            {t('COMMON.INTL_NUMBER_WITH_ONE_DECIMAL', { value: averageRating })}
          </div>
          <StarRatings
            rating={averageRating}
            starDimension="20px"
            starSpacing="1px"
            starRatedColor="blue"
            starEmptyColor="grey"
          />
          <div className={classes.starRatingLabel}>
            <FontAwesomeIcon icon={faUser} />
            <span className={classes.iconLabel}>
              {t('QUESTIONNAIRE.TOTAL_ANSWERS', { answers: totalAnswers })}
            </span>
          </div>
        </div>
        <ReactEcharts
          option={options}
          notMerge
          style={{ width: '100%', height: '100%' }}
        />
      </div>
    </div>
  );
};

QuestionRatingChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    question: PropTypes.string.isRequired,
    answers: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
    })).isRequired,
  })).isRequired,
};

export default QuestionRatingChart;
